import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query, QueryGetVisitorGroupsForTenantArgs } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_GROUPS } from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';
import { filterHiddenVisitorGroups } from 'v2/utils/visitorGroupOrigin';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_GROUPS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS;

const useVisitorsGroups = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: allVisitorGroupsLoading,
    error: allVisitorGroupsError,
    data: allVisitorGroupsData,
    refetch: refetchAllVisitorGroups
  } = useQuery(GET_ALL_VISITORS_GROUPS_TYPED);

  useEffect(() => {
    if (allVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(allVisitorGroupsError);
    }
  }, [allVisitorGroupsError]);

  const allVisitorGroups = useMemo(
    () => filterHiddenVisitorGroups(allVisitorGroupsData?.getVisitorGroupsForTenant ?? []),
    [allVisitorGroupsData]
  );

  return {
    allVisitorGroups,
    loadingAllVisitorGroups: allVisitorGroupsLoading && !allVisitorGroupsData,
    hasVisitorGroupsFetchError: Boolean(allVisitorGroupsError),
    // A failed retry is already reported through `allVisitorGroupsError`, so the rejection is only swallowed here.
    retryFetchingVisitorGroups: () => refetchAllVisitorGroups().catch(() => undefined)
  };
};

export default useVisitorsGroups;
